import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "@/api/client";
import { PublicNav } from "@/components/layout/PublicNav";

export function VerifyPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<"loading" | "set-password" | "success" | "error">(
    token ? "loading" : "error"
  );
  const [errorMsg, setErrorMsg] = useState(token ? "" : "No verification token provided.");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return; // status/errorMsg already initialised to the error state
    apiGet<{ verified: boolean }>(`/api/verify-email?token=${token}`)
      .then(() => setStatus("set-password"))
      .catch((e) => {
        if (e.message?.includes("already")) setStatus("set-password");
        else { setStatus("error"); setErrorMsg(e.message || "Verification failed."); }
      });
  }, [token]);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) { setErrorMsg("Password must be at least 6 characters"); return; }
    if (password !== confirmPassword) { setErrorMsg("Passwords don't match"); return; }
    setErrorMsg(""); setSaving(true);
    try {
      await apiPost("/api/set-password", { token, password });
      setStatus("success");
    } catch (err: any) { setErrorMsg(err.message || "Failed to set password"); }
    finally { setSaving(false); }
  };

  return (
    <div className="brand-page min-h-dvh">
      <PublicNav />
      <div className="relative flex items-center justify-center px-4 py-20">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-96 h-96 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(200,145,58,0.08) 0%, transparent 60%)" }} />
        <div className="relative w-full max-w-md p-6 sm:p-8 text-center" style={{ background: "var(--bp-surface)", border: "1px solid var(--bp-line)" }}>

          {status === "loading" && (
            <>
              <div className="w-12 h-12 border-3 rounded-full animate-spin mx-auto mb-4" style={{ borderColor: "rgba(200,145,58,0.2)", borderTopColor: "var(--bp-gold)" }} />
              <p style={{ color: "var(--bp-text-dim)" }}>Verifying your email...</p>
            </>
          )}

          {status === "set-password" && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: "rgba(40,218,144,0.1)" }}>
                <svg className="w-8 h-8" style={{ color: "#28da90" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-xl font-bold mb-2">Email Verified!</h1>
              <p className="text-sm mb-6" style={{ color: "var(--bp-text-dim)" }}>Set a password for future access to your dashboard.</p>
              {errorMsg && <div className="text-sm px-4 py-2 mb-4" style={{ background: "rgba(203,80,95,0.08)", border: "1px solid rgba(203,80,95,0.2)", color: "#cb505f" }}>{errorMsg}</div>}
              <form onSubmit={handleSetPassword} className="space-y-4 text-left">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--bp-text-dim)", fontFamily: "'Figtree', sans-serif" }}>Password</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    required minLength={6} autoFocus autoComplete="new-password"
                    className="w-full px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#c8913a]/40 focus:border-[#c8913a]/30"
                    style={{ background: "#28252a", border: "1px solid rgba(255,255,255,0.06)", color: "#f0ebe5", fontFamily: "'Figtree', sans-serif" }}
                    placeholder="At least 6 characters" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--bp-text-dim)", fontFamily: "'Figtree', sans-serif" }}>Confirm Password</label>
                  <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    required autoComplete="new-password"
                    className="w-full px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#c8913a]/40 focus:border-[#c8913a]/30"
                    style={{ background: "#28252a", border: "1px solid rgba(255,255,255,0.06)", color: "#f0ebe5", fontFamily: "'Figtree', sans-serif" }}
                    placeholder="Confirm your password" />
                </div>
                <button type="submit" disabled={saving}
                  className="w-full font-semibold px-4 py-2.5 text-sm transition-all hover:brightness-110 disabled:opacity-50"
                  style={{ background: "#c8913a", color: "#141215", fontFamily: "'Figtree', sans-serif" }}>
                  {saving ? "Setting password..." : "Set Password & Continue"}
                </button>
              </form>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: "rgba(40,218,144,0.1)" }}>
                <svg className="w-8 h-8" style={{ color: "#28da90" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-xl font-bold mb-2">You're All Set!</h1>
              <p className="text-sm mb-6" style={{ color: "var(--bp-text-dim)" }}>Your password has been saved. You can now log in.</p>
              <Link to="/login">
                <button className="w-full font-semibold px-4 py-2.5 text-sm transition-all hover:brightness-110"
                  style={{ background: "#c8913a", color: "#141215", fontFamily: "'Figtree', sans-serif" }}>
                  Go to Login
                </button>
              </Link>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: "rgba(203,80,95,0.1)" }}>
                <svg className="w-8 h-8" style={{ color: "#cb505f" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h1 className="text-xl font-bold mb-2">Verification Failed</h1>
              <p className="text-sm mb-6" style={{ color: "var(--bp-text-dim)" }}>{errorMsg}</p>
              <Link to="/login">
                <button className="w-full font-semibold px-4 py-2.5 text-sm transition-all hover:brightness-110"
                  style={{ background: "rgba(255,255,255,0.06)", color: "var(--bp-text)", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'Figtree', sans-serif" }}>
                  Go to Login
                </button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
